import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, ArrowLeft, FileText, CheckCircle, RefreshCw } from 'lucide-react';
import { AUTH } from '@/constants/testIds';
import { api } from '@/utils/api';

const LOGO_TRANSPARENT = 'https://customer-assets-39nsmqrw.emergentagent.net/job_dining-portal-119/artifacts/5i4j17yj_ChatGPT_Image_Aug_31__2026__07_35_46_PM-removebg-preview.png';

/** Shared login page — configured by `role` prop passed from the route. */
export default function Login({ role = 'diner' }) {
  // Email/password state (vendor + admin)
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Re-apply flow state (vendor only)
  const [isRejected, setIsRejected]         = useState(false);
  const [showReapply, setShowReapply]       = useState(false);
  const [reapplyFile, setReapplyFile]       = useState(null);
  const [reapplyFileName, setReapplyFileName] = useState('');
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [reapplySuccess, setReapplySuccess] = useState(false);
  const reapplyFileRef = useRef();

  const { login, loginWithToken, enterGuest } = useAuth();
  const navigate = useNavigate();

  const isDiner  = role === 'diner';
  const isVendor = role === 'vendor';
  const isAdmin  = role === 'admin';

  const subtitle = isAdmin ? 'Admin Portal' : isVendor ? 'Restaurant Partner' : 'Diner';

  // ── Vendor / Admin: email + password ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsRejected(false);
    setShowReapply(false);
    setLoading(true);
    try {
      const user = await login(email, password);
      const path = user.role === 'admin'
        ? '/admin'
        : user.role === 'vendor'
          ? '/vendor/dashboard'
          : '/diner/discover';
      navigate(path, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const isRej = err.response?.status === 403 && detail.toLowerCase().includes('rejected');
      if (isRej) {
        setIsRejected(true);
        setError(detail);
      } else {
        setError(typeof detail === 'string' && detail ? detail : 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Vendor: Re-apply with new FSSAI ─────────────────────────────────────────
  const handleReapply = async (e) => {
    e.preventDefault();
    if (!reapplyFile) { setError('Please select a new FSSAI license document.'); return; }
    setReapplyLoading(true);
    setError('');
    try {
      // 1. Upload new FSSAI doc
      const fd = new FormData();
      fd.append('file', reapplyFile);
      const uploadRes = await api.upload('/auth/upload-doc', fd);
      const fssai_license_url = uploadRes.data.url;
      // 2. Submit re-application
      await api.post('/auth/reapply', { email, password, fssai_license_url });
      setReapplySuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Re-application failed. Please try again.');
    } finally {
      setReapplyLoading(false);
    }
  };



  return (
    <div className="min-h-screen flex flex-col bg-[#f8f7fb]">
      {/* Header */}
      <div className="text-white px-5 pt-10 pb-8"
        style={{ background: 'linear-gradient(135deg, #3D0070 0%, #5B21B6 100%)' }}>
        <button onClick={() => navigate('/')} className="mb-4 text-white/70 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <img src={LOGO_TRANSPARENT} alt="Dine Around" className="h-10 w-auto object-contain"
            style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <h1 className="text-2xl font-extrabold text-white">Welcome back</h1>
        <p className="text-purple-200 text-sm mt-1">
          {subtitle} Sign In · <span className="italic opacity-75">Discover. Dine. Delight.</span>
        </p>
      </div>

      <div className="flex-1 px-5 py-8 max-w-md mx-auto w-full">

        {/* ── DINER: Google Sign-In only ────────────────────────────── */}
        {isDiner && (
          <div className="space-y-4">
            <a
              data-testid="google-signin-btn"
              href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
              className="flex items-center justify-center gap-3 w-full py-3 px-4 bg-white border border-gray-200 rounded-xl shadow-sm text-gray-700 text-sm font-semibold hover:shadow-md hover:border-gray-300 transition-all">
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </a>
          </div>
        )}

        {/* ── VENDOR / ADMIN: Email + Password ──────────────────────── */}
        {!isDiner && (
          <>
            {/* Re-apply success screen */}
            {reapplySuccess ? (
              <div className="text-center space-y-4 py-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-7 h-7 text-green-500" />
                </div>
                <h2 className="text-lg font-bold text-[#1E1B4B]">Re-application Submitted!</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Your new FSSAI document has been received. Our team will review it
                  and email you within 1–2 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={showReapply ? handleReapply : handleSubmit} className="space-y-4">
                {error && (
                  <div data-testid={AUTH.errorMsg} className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl">
                    {error}
                    {isRejected && isVendor && !showReapply && (
                      <button
                        type="button"
                        data-testid="show-reapply-btn"
                        onClick={() => { setShowReapply(true); setError(''); }}
                        className="mt-2 flex items-center gap-1.5 text-[#5B21B6] font-semibold hover:underline text-sm"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Re-apply with a new FSSAI document
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-[#1E1B4B] mb-1.5">Email</label>
                  <input
                    data-testid={AUTH.emailInput}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="da-input"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1B4B] mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      data-testid={AUTH.passwordInput}
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="da-input pr-10"
                      placeholder="••••••••"
                      required
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Re-apply: new FSSAI upload (shown only after rejection) */}
                {showReapply && isVendor && (
                  <div className="border-t border-dashed border-purple-200 pt-4 space-y-3">
                    <p className="text-sm font-semibold text-[#1E1B4B]">Upload New FSSAI License</p>
                    <div
                      data-testid="reapply-fssai-upload"
                      onClick={() => reapplyFileRef.current?.click()}
                      className={`w-full border-2 border-dashed rounded-xl px-4 py-4 flex items-center gap-3 cursor-pointer transition-colors ${
                        reapplyFile ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-[#5B21B6]'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        reapplyFile ? 'bg-green-100' : 'bg-purple-100'
                      }`}>
                        {reapplyFile
                          ? <CheckCircle className="w-5 h-5 text-green-500" />
                          : <FileText className="w-5 h-5 text-[#5B21B6]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1E1B4B] truncate">
                          {reapplyFile ? reapplyFileName : 'Select FSSAI document'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">PDF, JPG, or PNG · Max 10 MB</p>
                      </div>
                    </div>
                    <input
                      ref={reapplyFileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) { setReapplyFile(f); setReapplyFileName(f.name); }
                      }}
                    />
                  </div>
                )}

                <button
                  data-testid={showReapply ? 'submit-reapply-btn' : AUTH.submitBtn}
                  type="submit"
                  disabled={loading || reapplyLoading}
                  className="da-btn-primary w-full flex items-center justify-center gap-2 mt-2"
                >
                  {(loading || reapplyLoading) && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {loading || reapplyLoading
                    ? (reapplyLoading ? 'Submitting…' : 'Signing in…')
                    : showReapply ? 'Submit Re-application' : 'Sign In'}
                </button>

                {showReapply && (
                  <button type="button" onClick={() => { setShowReapply(false); setError(''); setIsRejected(false); }}
                    className="text-sm text-gray-400 hover:text-gray-600 w-full text-center transition-colors">
                    Cancel
                  </button>
                )}
              </form>
            )}
          </>
        )}

        {/* Register link — vendors only (diners are auto-registered via OTP) */}
        {isVendor && (
          <p className="text-center text-sm text-gray-500 mt-6">
            Don't have an account?{' '}
            <Link
              data-testid={AUTH.registerLink}
              to="/register?role=vendor"
              className="text-[#5B21B6] font-semibold"
            >
              Create one
            </Link>
          </p>
        )}

        {/* Browse as Guest — diner only */}
        {isDiner && (
          <div className="mt-4 text-center">
            <button
              data-testid="browse-as-guest-btn"
              onClick={() => { enterGuest(); navigate('/diner/discover'); }}
              className="text-sm text-purple-400 hover:text-purple-600 font-medium transition-colors"
            >
              Browse as Guest →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
